import { ref, watch, onBeforeUnmount, type ShallowRef } from 'vue'
import { storeToRefs } from 'pinia'
import useDetailPanelStore from '@/stores/detailPanel'

import {
  Cartesian3,
  VerticalOrigin,
  type Viewer,
  type Entity,
  HorizontalOrigin,
  CallbackProperty,
  CallbackPositionProperty,
  HeightReference,
} from 'cesium'

const JKTOWNS_ENTITY_ID = 'jktowns_rotating_icon'

export default function useJakartownsObserver(viewerRef: ShallowRef<Viewer | null>): void {
  const detailPanelStore = useDetailPanelStore()
  const { observerPosition, rotation, isPanelDisplayed } = storeToRefs(detailPanelStore)

  const observerEntity = ref<Entity | null>(null)
  let viewer: Viewer | null = null
  let isInitialized = false

  const initializeEntity = () => {
    if (isInitialized || !viewer || !observerPosition.value) return

    observerEntity.value = viewer.entities.add({
      id: JKTOWNS_ENTITY_ID,
      position: new CallbackPositionProperty(() => {
        if (!observerPosition.value) return Cartesian3.ZERO
        return Cartesian3.fromDegrees(
          observerPosition.value.lng,
          observerPosition.value.lat,
          -16
        )
      }, false),
      billboard: {
        image: '/ICON_map_location_V2.png',
        show: new CallbackProperty(() => isPanelDisplayed.value, false),
        width: 32,
        height: 32,
        horizontalOrigin: HorizontalOrigin.CENTER,
        verticalOrigin: VerticalOrigin.CENTER,
        rotation: new CallbackProperty(() => rotation.value, false),
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        heightReference: HeightReference.CLAMP_TO_GROUND,
        alignedAxis: Cartesian3.UNIT_Z
      },
    })

    isInitialized = true
  }

  watch(observerPosition, () => {
    if (viewer && isInitialized) {
      viewer.scene.requestRender()
    }
  }, { deep: true })

  watch(rotation, () => {
    if (viewer && isInitialized) {
      viewer.scene.requestRender()
    }
  })

  watch(isPanelDisplayed, () => {
    if (viewer && isInitialized) {
      viewer.scene.requestRender()
    }
  })

  watch(
    () => viewerRef.value,
    (newViewer) => {
      if (newViewer && !isInitialized) {
        viewer = newViewer
        initializeEntity()
      }
    },
    { immediate: true }
  )

  onBeforeUnmount(() => {
    const viewer = viewerRef.value
    if (!viewer) return;

    if (observerEntity.value) {
      viewer.entities.remove(observerEntity.value)
      observerEntity.value = null
    }
  })
}